import { animate, style, transition, trigger } from '@angular/animations';
import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  computed,
  HostListener,
  OnDestroy,
  OnInit,
  Signal,
  ViewEncapsulation,
  WritableSignal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { AppInsightsService, AuthService, Faq, LibModalService } from '@experian-digital/affordability-library';
import { CookieService } from '@experian-digital/affordability-library/src/lib/shared/services/lib-cookie.service';
import { PendoService } from '@experian-digital/affordability-library/src/lib/shared/services/pendo.service';
import { getClientSettings } from '@portal-shared/configs/auth-config';
import { STANDARD_LOADER } from '@portal-shared/models/enums/loading.enum';
import { ApplicationConfigService } from '@portal-shared/services/config.service';
import { HeaderService } from '@portal-shared/services/header.service';
import { TimeoutService } from '@portal-shared/services/timeout.service';
import { WindowRef } from '@portal-shared/services/window.service';
import { Error, Loading, STORE_FIELDS, StoreState } from '@portal-store/models/store.model';
import { StoreService } from '@portal-store/store.service';
import { OAuthService } from 'angular-oauth2-oidc';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { FaqConfig } from './advisor/config/faq.config';

@Component({
  selector: 'aff-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('inOutAnimation', [
      transition(':enter', [style({ opacity: 0 }), animate('3s ease-out', style({ opacity: 1 }))]),
      transition(':leave', [style({ opacity: 1 }), animate('5s ease-in', style({ opacity: 0 }))]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AppComponent implements OnInit, AfterViewChecked, OnDestroy {
  subscriptions: Subscription[] = [];
  isScrollToTopIconVisible = false;
  loadingInitial$: Observable<Loading>;
  error$: Observable<Error>;
  showFaq: boolean = true;
  currentRoute: Subject<unknown> = new Subject();
  isFaqModalVisible = false;
  faqList: Faq[] = new FaqConfig().getFaqs();
  hideNavBar = false;
  detectedHeaderHeight: WritableSignal<number> = this.headerService.headerHeight;
  cookieConsentName = '__affordabilityPortalCookieConsent';
  headerHeight: Signal<string> = computed(() => {
    return (this.detectedHeaderHeight() + 28).toString() + 'px';
  });
  isAuthenticated$: Observable<boolean> = this.authService.isAuthenticated$;

  constructor(
    private cd: ChangeDetectorRef,
    private router: Router,
    private store: StoreService,
    private oAuthService: OAuthService,
    private authService: AuthService,
    private timeoutService: TimeoutService,
    public appInsights: AppInsightsService,
    private headerService: HeaderService,
    private windowRef: WindowRef,
    private pendoService: PendoService,
  ) {
    this.sessionTimeoutListener();
  }

  @HostListener('mouseenter')
  @HostListener('mouseover')
  @HostListener('mousemove')
  sessionTimeoutListener(): void {
    this.timeoutService.initTimer();
  }
  @HostListener('beforeunload')
  beforeUnloadListener(): void {
    this.timeoutService.beforeunloadListener();
  }

  @HostListener('window:scroll', [])
  onScroll(): void {
    if (window.scrollY >= 200) {
      this.isScrollToTopIconVisible = true;
      this.cd.detectChanges();
      return;
    }
    this.isScrollToTopIconVisible = false;
    this.cd.detectChanges();
  }

  ngAfterViewChecked(): void {
    this.cd.detectChanges();
  }

  ngOnInit(): void {
    this.setUpCookieConsent();
    this.oAuthService.configure(getClientSettings());
    this.authService.runInitialLoginSequence();
    this.resetErrorInStoreOnPageChange();
    this.store.add<Loading>(STORE_FIELDS.loadingInitial, STANDARD_LOADER);
    this.store.add<Error>(STORE_FIELDS.error, null);
    this.loadingInitial$ = this.getStoreState(STORE_FIELDS.loadingInitial);
    this.error$ = this.getStoreState<Error>(STORE_FIELDS.error);

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.store.add(STORE_FIELDS.loading, STANDARD_LOADER);
        if (event.url.includes('work-report')) {
          this.showFaq = false;
        }
      } else if (event instanceof NavigationEnd) {
        this.store.add<Loading>(STORE_FIELDS.loading, null);
        this.store.add<Loading>(STORE_FIELDS.loadingInitial, null);
        this.hideNavBar = false;
        if (event.url.includes('select-department')) {
          this.hideNavBar = true;
        }
      }
    });
  }
  navigateTo(): void {
    const error = this.store.get<Error>(STORE_FIELDS.error);
    this.router.navigateByUrl(error.errorLink);
    this.store.add<Error>(STORE_FIELDS.error, null);
  }

  resetErrorInStoreOnPageChange(): void {
    this.subscriptions.push(
      this.router.events.pipe(filter((event) => event instanceof NavigationStart)).subscribe(() => {
        this.store.add(STORE_FIELDS.error, null);
      }),
    );
  }

  scrollToTop($element?): void {
    if ($element) {
      $element.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
      return;
    }
    window.scroll(0, 0);
  }

  isOverlayVisible(): BehaviorSubject<boolean> {
    return LibModalService.isOverlayVisible;
  }

  setClickedCookieConsent(consent: boolean): void {
    this.updateCookieAndPanel(consent);
  }

  private isPendoEnabled(): boolean {
    return ApplicationConfigService.config.enablePendo === 'true';
  }

  private togglePendo(cookieConsent: boolean): void {
    if (this.isPendoEnabled()) {
      if (cookieConsent) {
        this.pendoService.initPendo(true, ApplicationConfigService.config.pendoApiKey);
      } else {
        this.pendoService.revokePendoConsent();
      }
    }
  }

  private updateCookieAndPanel(cookiesEnabled: boolean): void {
    CookieService.setCookie(this.cookieConsentName, cookiesEnabled);
    CookieService.setCookiePanelVisibility(this.cookieConsentName);
    this.updateAppInsightsCookieConsent(cookiesEnabled);
    this.togglePendo(cookiesEnabled);
  }

  private setUpCookieConsent(): void {
    // NOTE: this listens for local storage entries so we
    // can keep track of changes on the cookie policy route.
    this.windowRef.nativeWindow.addEventListener(
      'storage',
      (message): void => this.handleCookieMessage(message),
      false,
    );
    const consentCookie = CookieService.readCookie(this.cookieConsentName);
    if (environment.production) {
      this.appInsights.initAppInsights(ApplicationConfigService.config.instrumentationKey, consentCookie ?? false);
    }
    if (this.isPendoEnabled() && consentCookie !== null) {
      this.togglePendo(consentCookie);
    }
  }

  private handleCookieMessage(message: any): void {
    if (message.key === 'sendCookieDecision' && message.newValue !== null) {
      const consent: boolean = JSON.parse(message.newValue);
      this.updateCookieAndPanel(consent);
      localStorage.removeItem('sendCookieDecision');
    }
  }

  private updateAppInsightsCookieConsent(consent: boolean): void {
    if (environment.production) {
      this.appInsights.enableCookies(consent);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  private getStoreState<T>(storeField: string): Observable<T> {
    return this.store.globalStateChanged.pipe(map((store: StoreState) => store && store[storeField]));
  }
}
